import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTaskStatus } from './api';
import Tooltip from './Tooltip';

function safeLower(s) {
  return String(s || '').toLowerCase();
}

function parseTimecodeToMs(raw) {
  const s = String(raw || '').trim().replace(',', '.');
  const m = s.match(/^(\d{1,2}):(\d{2}):(\d{2})[.](\d{1,3})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const ss = Number(m[3]);
  const ms = Number(String(m[4]).padEnd(3, '0'));
  if (![hh, mm, ss, ms].every((v) => Number.isFinite(v))) return null;
  return ((hh * 60 + mm) * 60 + ss) * 1000 + ms;
}

function formatMsAsTime(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '00:00';
  const totalSeconds = Math.floor(n / 1000);
  const mm = Math.floor(totalSeconds / 60);
  const ss = totalSeconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function formatSrtTime(ms) {
  const n = Number(ms);
  const totalMs = Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
  const msPart = totalMs % 1000;
  const totalSeconds = Math.floor(totalMs / 1000);
  const ss = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const mm = totalMinutes % 60;
  const hh = Math.floor(totalMinutes / 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')},${String(msPart).padStart(
    3,
    '0'
  )}`;
}

function parseSrt(text) {
  const src = String(text || '').replace(/\uFEFF/g, '').replace(/\r/g, '');
  const blocks = src.split(/\n\s*\n/g);
  const cues = [];
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    const lines = trimmed.split('\n');
    if (lines.length < 2) continue;

    let cursor = 0;
    const maybeIndex = Number(String(lines[0] || '').trim());
    if (Number.isFinite(maybeIndex) && String(lines[0] || '').trim() === String(maybeIndex)) cursor = 1;

    const timeLine = String(lines[cursor] || '').trim();
    const m = timeLine.match(
      /(\d{1,2}:\d{2}:\d{2}[.,]\d{1,3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}[.,]\d{1,3})/
    );
    if (!m) continue;
    const startMs = parseTimecodeToMs(m[1]);
    const endMs = parseTimecodeToMs(m[2]);
    if (startMs == null || endMs == null) continue;

    const body = lines.slice(cursor + 1).join('\n').trim();
    cues.push({
      index: Number.isFinite(maybeIndex) ? maybeIndex : cues.length + 1,
      startMs,
      endMs,
      text: body,
    });
  }
  return cues;
}

function buildSrt(cues) {
  const list = Array.isArray(cues) ? cues : [];
  const blocks = list.map((cue, i) => {
    const start = formatSrtTime(cue.startMs);
    const end = formatSrtTime(cue.endMs);
    const body = String(cue.text || '').replace(/\r/g, '').trim();
    return `${i + 1}\n${start} --> ${end}\n${body}\n`;
  });
  return blocks.join('\n').trim() + '\n';
}

function formatAssTime(seconds) {
  const n = Number(seconds);
  const s = Number.isFinite(n) && n >= 0 ? n : 0;
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = (s % 60).toFixed(2);
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(5, '0')}`;
}

function escapeAssText(text) {
  const s = String(text || '');
  return s
    .replace(/\{/g, '(')
    .replace(/\}/g, ')')
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '\\N')
    .replace(/\\n/g, '\\N');
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function fetchText(url) {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.text();
}

function pickDefaultLeft(srtOutputs) {
  if (!Array.isArray(srtOutputs) || srtOutputs.length === 0) return null;
  return (
    srtOutputs.find((o) => o?.kind === 'source_srt') ||
    srtOutputs.find((o) => safeLower(o?.filename).includes('.en.')) ||
    srtOutputs[0]
  );
}

function pickDefaultRight(srtOutputs, left) {
  if (!Array.isArray(srtOutputs) || srtOutputs.length === 0) return null;
  const leftName = left?.filename ? String(left.filename) : '';
  const candidate =
    srtOutputs.find((o) => o?.kind === 'translated_srt') ||
    srtOutputs.find((o) => safeLower(o?.filename).includes('.zh-')) ||
    srtOutputs.find((o) => o && o.filename !== leftName) ||
    srtOutputs[0];
  return candidate;
}

function pickDefaultKaraoke(outputs) {
  const list = Array.isArray(outputs) ? outputs : [];
  return (
    list.find((o) => safeLower(o?.kind) === 'karaoke_ass') ||
    list.find((o) => safeLower(o?.filename).includes('.karaoke.') && safeLower(o?.filename).endsWith('.ass')) ||
    list.find((o) => safeLower(o?.filename).endsWith('.karaoke.ass')) ||
    null
  );
}

function binarySearchLastLE(arr, value) {
  // Returns last index i such that arr[i] <= value. Assumes arr is non-decreasing.
  let lo = 0;
  let hi = arr.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] <= value) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

function binarySearchNearest(arr, value) {
  // Returns index of the nearest value in a sorted arr.
  let lo = 0;
  let hi = arr.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  const i = lo;
  const prev = i > 0 ? i - 1 : i;
  if (Math.abs(arr[prev] - value) <= Math.abs(arr[i] - value)) return prev;
  return i;
}

function lowerBound(arr, value) {
  // First index i such that arr[i] >= value. Assumes arr is non-decreasing.
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function findOverlappingCueIndices(cues, startMs, endMs) {
  // Overlap test: cue.start < end && cue.end > start
  const list = Array.isArray(cues) ? cues : [];
  if (!list.length) return [];
  const start = Number(startMs);
  const end = Number(endMs);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

  const starts = list.map((c) => c.startMs);
  const idxStart = lowerBound(starts, start);
  const indices = [];

  // Scan backward to include cues that start before `start` but extend into it.
  for (let j = idxStart - 1; j >= 0; j--) {
    const c = list[j];
    if (!(c && Number.isFinite(c.endMs))) break;
    if (c.endMs <= start) break;
    indices.unshift(j);
  }

  // Scan forward until starts pass `end`.
  for (let i = idxStart; i < list.length; i++) {
    const c = list[i];
    if (!(c && Number.isFinite(c.startMs) && Number.isFinite(c.endMs))) continue;
    if (c.startMs >= end) break;
    if (c.endMs > start) indices.push(i);
  }

  // De-dupe while preserving order.
  const seen = new Set();
  const unique = [];
  for (const i of indices) {
    if (seen.has(i)) continue;
    seen.add(i);
    unique.push(i);
  }
  return unique;
}

function computeElementTops(container, elements) {
  if (!container) return [];
  const baseTop = container.getBoundingClientRect().top;
  let last = 0;
  return elements.map((el) => {
    if (!el) return last;
    const rect = el.getBoundingClientRect();
    const top = rect.top - baseTop + container.scrollTop;
    if (!Number.isFinite(top)) return last;
    last = top;
    return top;
  });
}

function insertTranslationIntoAss(karaokeAssText, translationCues, targetStyleName) {
  const base = String(karaokeAssText || '').replace(/\r/g, '');
  const lines = base.split('\n');
  const eventsIdx = lines.findIndex((l) => String(l || '').trim() === '[Events]');
  if (eventsIdx < 0) return base;
  const formatRel = lines.slice(eventsIdx + 1).findIndex((l) => String(l || '').startsWith('Format:'));
  const formatIdx = formatRel >= 0 ? eventsIdx + 1 + formatRel : -1;
  const insertAt = formatIdx >= 0 ? formatIdx + 1 : eventsIdx + 1;

  const style = String(targetStyleName || 'Target').trim() || 'Target';
  const cues = Array.isArray(translationCues) ? translationCues : [];
  const dialogueLines = cues.map((cue) => {
    const start = formatAssTime((cue.startMs || 0) / 1000);
    const end = formatAssTime((cue.endMs || 0) / 1000);
    const text = escapeAssText(String(cue.text || '').trim());
    return `Dialogue: 0,${start},${end},${style},,0,0,0,,${text}`;
  });

  const out = lines.slice();
  out.splice(insertAt, 0, ...dialogueLines);
  return out.join('\n').replace(/\n+$/, '\n');
}

export default function EditorPage({ tasks, onRefreshTasks }) {
  const { t } = useTranslation();

  const [taskId, setTaskId] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      return params.get('task') || params.get('task_id') || '';
    } catch (_) {
      return '';
    }
  });

  const [loadingTask, setLoadingTask] = useState(false);
  const [error, setError] = useState('');
  const [task, setTask] = useState(null);

  const [leftFile, setLeftFile] = useState(null);
  const [rightFile, setRightFile] = useState(null);
  const [karaokeFile, setKaraokeFile] = useState(null);

  const [leftText, setLeftText] = useState('');
  const [rightOriginalText, setRightOriginalText] = useState('');
  const [karaokeAssText, setKaraokeAssText] = useState('');

  const [rightCues, setRightCues] = useState([]);
  const [originalRightCues, setOriginalRightCues] = useState([]);
  const [draftStatus, setDraftStatus] = useState('');
  const [dirty, setDirty] = useState(false);

  const [syncScrollEnabled, setSyncScrollEnabled] = useState(true);
  const [wrapEnabled, setWrapEnabled] = useState(true);
  const [activeLeftIndex, setActiveLeftIndex] = useState(-1);
  const [activeRightIndex, setActiveRightIndex] = useState(-1);
  const [linkedLeftIndices, setLinkedLeftIndices] = useState([]);
  const [linkedRightIndices, setLinkedRightIndices] = useState([]);

  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const leftCueElsRef = useRef([]);
  const rightCueElsRef = useRef([]);
  const leftCueTopsRef = useRef([]);
  const rightCueTopsRef = useRef([]);
  const syncingRef = useRef(false);
  const draftTimerRef = useRef(null);
  const topsTimerRef = useRef(null);

  const selectOptions = useMemo(() => {
    const list = Array.isArray(tasks) ? tasks.slice().reverse() : [];
    return list;
  }, [tasks]);

  const outputs = useMemo(() => (Array.isArray(task?.outputs) ? task.outputs : []), [task]);
  const srtOutputs = useMemo(() => outputs.filter((o) => safeLower(o?.filename).endsWith('.srt')), [outputs]);
  const translatedSrtOutputs = useMemo(
    () => srtOutputs.filter((o) => safeLower(o?.kind) === 'translated_srt'),
    [srtOutputs]
  );

  const leftCues = useMemo(() => parseSrt(leftText), [leftText]);
  const leftTimes = useMemo(() => leftCues.map((c) => c.startMs), [leftCues]);
  const rightTimes = useMemo(() => rightCues.map((c) => c.startMs), [rightCues]);
  const linkedLeftSet = useMemo(() => new Set(linkedLeftIndices || []), [linkedLeftIndices]);
  const linkedRightSet = useMemo(() => new Set(linkedRightIndices || []), [linkedRightIndices]);

  const draftKey = useMemo(() => {
    const id = String(taskId || '').trim();
    const name = String(rightFile?.filename || '').trim();
    if (!id || !name) return '';
    return `gongju_sub_editor_draft_v1:${id}:${name}`;
  }, [taskId, rightFile]);

  const loadTask = useCallback(async () => {
    const id = String(taskId || '').trim();
    if (!id) return;
    setLoadingTask(true);
    setError('');
    try {
      const resp = await getTaskStatus(id);
      const data = resp?.data || null;
      setTask(data);

      const srt = Array.isArray(data?.outputs) ? data.outputs.filter((o) => safeLower(o?.filename).endsWith('.srt')) : [];
      const left = pickDefaultLeft(srt);
      const right = pickDefaultRight(srt.filter((o) => safeLower(o?.kind) === 'translated_srt'), left);
      setLeftFile(left);
      setRightFile(right);

      const karaoke = pickDefaultKaraoke(Array.isArray(data?.outputs) ? data.outputs : []);
      setKaraokeFile(karaoke);
    } catch (e) {
      console.error('Failed to load editor task:', e);
      setTask(null);
      setLeftFile(null);
      setRightFile(null);
      setKaraokeFile(null);
      setLeftText('');
      setRightOriginalText('');
      setKaraokeAssText('');
      setRightCues([]);
      setOriginalRightCues([]);
      setDraftStatus('');
      setError(t('review.loadTaskFailed'));
    } finally {
      setLoadingTask(false);
    }
  }, [taskId, t]);

  // Keep URL ?task= in sync so users can share/bookmark the editor link.
  useEffect(() => {
    const id = String(taskId || '').trim();
    if (!id) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('task', id);
      window.history.replaceState({}, '', url.toString());
    } catch (_) {
      // no-op
    }
  }, [taskId]);

  // Auto-load when visiting /subtranslator/editor?task=<id>
  useEffect(() => {
    const id = String(taskId || '').trim();
    if (id) loadTask();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!leftFile?.url) {
        setLeftText('');
        return;
      }
      try {
        const text = await fetchText(leftFile.url);
        if (!cancelled) setLeftText(text);
      } catch (e) {
        console.error('Failed to fetch left SRT:', e);
        if (!cancelled) setLeftText(t('review.loadSrtFailed'));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [leftFile, t]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!rightFile?.url) {
        setRightOriginalText('');
        setRightCues([]);
        setOriginalRightCues([]);
        setDraftStatus('');
        return;
      }
      try {
        const text = await fetchText(rightFile.url);
        if (cancelled) return;
        setRightOriginalText(text);

        const original = parseSrt(text);
        setOriginalRightCues(original);
        setDirty(false);

        // Load local draft if present.
        if (draftKey) {
          try {
            const saved = String(window.localStorage.getItem(draftKey) || '').trim();
            if (saved) {
              const draft = parseSrt(saved);
              if (draft.length) {
                setRightCues(draft);
                setDraftStatus(t('editor.draftLoaded'));
                setDirty(false);
                return;
              }
            }
          } catch (_) {
            // ignore
          }
        }
        setRightCues(original);
        setDraftStatus('');
        setDirty(false);
      } catch (e) {
        console.error('Failed to fetch right SRT:', e);
        if (!cancelled) {
          setRightOriginalText('');
          setRightCues([]);
          setOriginalRightCues([]);
          setDraftStatus('');
          setDirty(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [rightFile, draftKey, t]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!karaokeFile?.url) {
        setKaraokeAssText('');
        return;
      }
      try {
        const text = await fetchText(karaokeFile.url);
        if (!cancelled) setKaraokeAssText(text);
      } catch (e) {
        console.error('Failed to fetch karaoke ASS:', e);
        if (!cancelled) setKaraokeAssText('');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [karaokeFile]);

  // Auto-save local draft (debounced).
  useEffect(() => {
    if (!draftKey) return;
    if (!rightCues || rightCues.length === 0) return;
    if (!dirty) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, buildSrt(rightCues));
        setDraftStatus(t('editor.draftSaved'));
      } catch (_) {
        // ignore storage failures
      }
    }, 500);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [draftKey, rightCues, dirty, t]);

  const recomputeCueTops = useCallback(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    leftCueTopsRef.current = computeElementTops(left, leftCueElsRef.current || []);
    rightCueTopsRef.current = computeElementTops(right, rightCueElsRef.current || []);
  }, []);

  useEffect(() => {
    if (topsTimerRef.current) clearTimeout(topsTimerRef.current);
    topsTimerRef.current = setTimeout(() => {
      requestAnimationFrame(recomputeCueTops);
    }, 120);
    return () => {
      if (topsTimerRef.current) clearTimeout(topsTimerRef.current);
    };
  }, [leftCues, rightCues, wrapEnabled, recomputeCueTops]);

  useEffect(() => {
    const onResize = () => recomputeCueTops();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeCueTops]);

  const scrollToIndex = useCallback((side, index) => {
    const idx = Number(index);
    if (!Number.isFinite(idx) || idx < 0) return;
    const container = side === 'left' ? leftRef.current : rightRef.current;
    const tops = side === 'left' ? leftCueTopsRef.current : rightCueTopsRef.current;
    if (!container || !Array.isArray(tops) || idx >= tops.length) return;
    const targetTop = Number(tops[idx]);
    if (!Number.isFinite(targetTop)) return;

    syncingRef.current = true;
    container.scrollTop = Math.max(0, targetTop - 8);
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  }, []);

  const handleCueClick = useCallback(
    (side, idx) => {
      if (side === 'left') {
        const cue = leftCues[idx];
        if (!cue) return;
        setActiveLeftIndex(idx);
        setActiveRightIndex(-1);
        setLinkedLeftIndices([]);

        let matches = findOverlappingCueIndices(rightCues, cue.startMs, cue.endMs);
        if (!matches.length && rightTimes.length) matches = [binarySearchNearest(rightTimes, cue.startMs)];
        setLinkedRightIndices(matches);
        if (matches.length) scrollToIndex('right', matches[0]);
        return;
      }

      const cue = rightCues[idx];
      if (!cue) return;
      setActiveRightIndex(idx);
      setActiveLeftIndex(-1);
      setLinkedRightIndices([]);

      let matches = findOverlappingCueIndices(leftCues, cue.startMs, cue.endMs);
      if (!matches.length && leftTimes.length) matches = [binarySearchNearest(leftTimes, cue.startMs)];
      setLinkedLeftIndices(matches);
      if (matches.length) scrollToIndex('left', matches[0]);
    },
    [leftCues, rightCues, leftTimes, rightTimes, scrollToIndex]
  );

  const syncFrom = (side) => {
    if (!syncScrollEnabled) return;
    if (syncingRef.current) return;
    const a = side === 'left' ? leftRef.current : rightRef.current;
    const b = side === 'left' ? rightRef.current : leftRef.current;
    if (!a || !b) return;
    const aCues = side === 'left' ? leftCues : rightCues;
    const bCues = side === 'left' ? rightCues : leftCues;
    const aTops = side === 'left' ? leftCueTopsRef.current : rightCueTopsRef.current;
    const bTops = side === 'left' ? rightCueTopsRef.current : leftCueTopsRef.current;
    const bTimes = side === 'left' ? rightTimes : leftTimes;

    if (!aCues.length || !bCues.length || !aTops.length || !bTops.length || !bTimes.length) return;

    const anchorTop = a.scrollTop + 8;
    const aIndex = binarySearchLastLE(aTops, anchorTop);
    const timeMs = aCues[aIndex]?.startMs ?? 0;
    const bIndex = binarySearchNearest(bTimes, timeMs);
    const targetTop = bTops[bIndex] ?? 0;

    syncingRef.current = true;
    b.scrollTop = targetTop;
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  const downloadEditedSrt = useCallback(() => {
    if (!rightCues.length) return;
    const lang = String(rightFile?.language || '').trim();
    const suffix = lang ? `edited.${lang}` : 'edited';
    downloadTextFile(`${String(taskId || 'task')}.${suffix}.srt`, buildSrt(rightCues));
  }, [rightCues, rightFile, taskId]);

  const restoreOriginal = useCallback(() => {
    setRightCues(originalRightCues);
    setDirty(false);
    setDraftStatus(t('editor.restored'));
    if (draftKey) {
      try {
        window.localStorage.removeItem(draftKey);
      } catch (_) {
        // ignore
      }
    }
  }, [draftKey, originalRightCues, t]);

  const downloadMergedAss = useCallback(() => {
    if (!karaokeAssText.trim() || !rightCues.length) return;
    const merged = insertTranslationIntoAss(karaokeAssText, rightCues, 'Target');
    const lang = String(rightFile?.language || '').trim();
    const suffix = lang ? `merged.${lang}` : 'merged';
    downloadTextFile(`${String(taskId || 'task')}.${suffix}.ass`, merged);
  }, [karaokeAssText, rightCues, rightFile, taskId]);

  return (
    <div className="ReviewPage">
      <div className="Row Row--between Row--center ReviewTop">
        <div>
          <div className="CardTitle">{t('review.title')}</div>
          <div className="SmallNote">{t('review.hint')}</div>
          {draftStatus && <div className="SmallNote">{draftStatus}</div>}
        </div>
        <div className="Row Row--center">
          <a className="Btn Btn--ghost Btn--small" href="/subtranslator/">
            {t('review.back')}
          </a>
          <button className="Btn Btn--ghost Btn--small" type="button" onClick={onRefreshTasks}>
            {t('button.refresh')}
          </button>
        </div>
      </div>

      <div className="Card ReviewControls">
        <div className="Row Row--wrap Row--center">
          <label className="Field Field--inline ReviewField">
            <span className="FieldLabel">{t('review.taskId')}</span>
            <input
              type="text"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder={t('review.taskIdPlaceholder')}
              autoComplete="off"
            />
          </label>
          <button
            className="Btn Btn--primary"
            type="button"
            disabled={loadingTask || !String(taskId || '').trim()}
            onClick={loadTask}
          >
            {loadingTask ? t('review.loading') : t('review.load')}
          </button>
          <button className="Btn Btn--ghost Btn--small" type="button" onClick={downloadEditedSrt} disabled={!rightCues.length}>
            {t('editor.downloadEditedSrt')}
          </button>
          <Tooltip text={t('editor.downloadMergedAssHint')}>
            <button
              className="Btn Btn--ghost Btn--small"
              type="button"
              onClick={downloadMergedAss}
              disabled={!rightCues.length || !karaokeAssText.trim()}
            >
              {t('editor.downloadMergedAss')}
            </button>
          </Tooltip>
          <button
            className="Btn Btn--ghost Btn--small"
            type="button"
            onClick={restoreOriginal}
            disabled={!originalRightCues.length}
          >
            {t('editor.restore')}
          </button>
        </div>

        <label className="Field">
          <div className="FieldLabel">{t('review.selectFromTasks')}</div>
          <select value={String(taskId || '')} onChange={(e) => setTaskId(e.target.value)}>
            <option value="">{t('review.selectPlaceholder')}</option>
            {selectOptions.map((it) => (
              <option key={it.id} value={it.id}>
                {it.input_name || it.input_url || it.id}
              </option>
            ))}
          </select>
        </label>

        {error && <div className="ReviewError">{error}</div>}
      </div>

      {task && srtOutputs.length > 0 && (
        <div className="Card ReviewPickers">
          <div className="Row Row--wrap">
            <label className="Field Field--inline ReviewField">
              <span className="FieldLabel">{t('review.leftFile')}</span>
              <select
                value={leftFile?.filename || ''}
                onChange={(e) => {
                  const name = e.target.value;
                  const found = srtOutputs.find((o) => o?.filename === name) || null;
                  setLeftFile(found);
                }}
              >
                {srtOutputs.map((o) => (
                  <option key={`left-${o.filename}`} value={o.filename}>
                    {o.label || o.filename}
                  </option>
                ))}
              </select>
            </label>

            <label className="Field Field--inline ReviewField">
              <span className="FieldLabel">{t('review.rightFile')}</span>
              <select
                value={rightFile?.filename || ''}
                onChange={(e) => {
                  const name = e.target.value;
                  const found = translatedSrtOutputs.find((o) => o?.filename === name) || null;
                  setRightFile(found);
                }}
              >
                {translatedSrtOutputs.map((o) => (
                  <option key={`right-${o.filename}`} value={o.filename}>
                    {o.label || o.filename}
                  </option>
                ))}
              </select>
            </label>

            {karaokeFile && (
              <label className="Field Field--inline ReviewField">
                <span className="FieldLabel">{t('editor.karaokeFile')}</span>
                <select value={karaokeFile?.filename || ''} onChange={() => {}}>
                  <option value={karaokeFile.filename}>{karaokeFile.label || karaokeFile.filename}</option>
                </select>
              </label>
            )}
          </div>

          <div className="Row Row--wrap ReviewToggles">
            <label className="Check">
              <input type="checkbox" checked={syncScrollEnabled} onChange={(e) => setSyncScrollEnabled(e.target.checked)} />
              <span>{t('review.syncScroll')}</span>
            </label>
            <label className="Check">
              <input type="checkbox" checked={wrapEnabled} onChange={(e) => setWrapEnabled(e.target.checked)} />
              <span>{t('review.wrap')}</span>
            </label>
          </div>
        </div>
      )}

      <div className="ReviewGrid">
        <div className="Card ReviewPane">
          <div className="Row Row--between Row--center ReviewPaneHeader">
            <div className="ReviewPaneTitle">{t('review.source')}</div>
            {leftFile?.url && (
              <a className="Chip" href={leftFile.url} target="_blank" rel="noopener noreferrer">
                {t('review.open')}
              </a>
            )}
          </div>
          <div
            ref={leftRef}
            className={`ReviewScroll ${wrapEnabled ? '' : 'ReviewScroll--nowrap'}`}
            onScroll={() => syncFrom('left')}
            role="region"
            aria-label={t('review.source')}
          >
            {leftCues.length > 0 ? (
              leftCues.map((cue, idx) => (
                <div
                  key={`l-${cue.startMs}-${cue.endMs}-${idx}`}
                  className={[
                    'ReviewCue',
                    idx === activeLeftIndex ? 'ReviewCue--active' : '',
                    linkedLeftSet.has(idx) ? 'ReviewCue--linked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  ref={(el) => {
                    leftCueElsRef.current[idx] = el;
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCueClick('left', idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleCueClick('left', idx);
                  }}
                >
                  <div className="ReviewCueMeta">
                    <span className="ReviewCueTime">
                      {formatMsAsTime(cue.startMs)}–{formatMsAsTime(cue.endMs)}
                    </span>
                  </div>
                  <div className="ReviewCueText">{cue.text}</div>
                </div>
              ))
            ) : (
              <div className="ReviewEmpty">{leftText ? t('review.loadSrtFailed') : t('review.waiting')}</div>
            )}
          </div>
        </div>

        <div className="Card ReviewPane">
          <div className="Row Row--between Row--center ReviewPaneHeader">
            <div className="ReviewPaneTitle">{t('review.translation')}</div>
            {rightFile?.url && (
              <a className="Chip" href={rightFile.url} target="_blank" rel="noopener noreferrer">
                {t('review.open')}
              </a>
            )}
          </div>
          <div
            ref={rightRef}
            className={`ReviewScroll ${wrapEnabled ? '' : 'ReviewScroll--nowrap'}`}
            onScroll={() => syncFrom('right')}
            role="region"
            aria-label={t('review.translation')}
          >
            {rightCues.length > 0 ? (
              rightCues.map((cue, idx) => (
                <div
                  key={`r-${cue.startMs}-${cue.endMs}-${idx}`}
                  className={[
                    'ReviewCue',
                    idx === activeRightIndex ? 'ReviewCue--active' : '',
                    linkedRightSet.has(idx) ? 'ReviewCue--linked' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  ref={(el) => {
                    rightCueElsRef.current[idx] = el;
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCueClick('right', idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleCueClick('right', idx);
                  }}
                >
                  <div className="ReviewCueMeta">
                    <span className="ReviewCueTime">
                      {formatMsAsTime(cue.startMs)}–{formatMsAsTime(cue.endMs)}
                    </span>
                  </div>
                  <textarea
                    className="ReviewCueInput"
                    value={cue.text}
                    rows={2}
                    onFocus={() => handleCueClick('right', idx)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRightCues((prev) => {
                        const next = Array.isArray(prev) ? prev.slice() : [];
                        const cur = next[idx];
                        if (!cur) return prev;
                        next[idx] = { ...cur, text: value };
                        return next;
                      });
                      setDirty(true);
                    }}
                  />
                </div>
              ))
            ) : (
              <div className="ReviewEmpty">{rightOriginalText ? t('review.loadSrtFailed') : t('review.waiting')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
